import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import RelatedLinks from '../components/RelatedLinks';
import { providerTypes } from '../../enums/ProviderTypes';

/**
 * Provider — the venue operator behind facilities and products.
 *
 * The old page is an `EditableArea`: read-only until you press Edit, then a form,
 * then Save. Two modes to maintain and a click before you can fix a typo. Here
 * the fields are the form, Save is disabled until something actually changed, and
 * Discard puts back what the server last sent — which is the same safety the two
 * modes were providing, without the mode.
 *
 * Its validation rules are carried exactly (five fields, 2–256 characters,
 * category required) and shown per field as you type rather than as one summary
 * line naming the fields that failed.
 *
 * `Category` travels as an **array** into `editProviderData`, which does
 * `Category.join(',')` before posting — pass it the string the form holds and the
 * save dies with "Category.join is not a function". It arrives as an array too,
 * so the form keeps the readable joined form and splits it back on save.
 *
 * A provider can carry several categories while the picker offers one at a time,
 * which is also true of the old page. Rather than let the current value vanish
 * from a select that cannot represent it, it is offered as its own option: the
 * form never silently drops a category nobody chose to drop.
 *
 * `/next/provider/new` is this same page with nothing loaded, the way `Area` does
 * it. The old admin has a second file for creating one (`ProviderAdd.js`), with
 * its own copy of the same five validation rules and the same fields — which is
 * the arrangement that guarantees the two forms eventually disagree. One page
 * cannot.
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const TYPES = Object.keys(providerTypes);

// The old page's Yup schema, field for field.
const required = (value, label) => {
  const text = String(value ?? '').trim();
  if (!text) return `${label} is required.`;
  if (text.length < 2) return `${label} should be at least 2 characters.`;
  if (text.length > 256) return `${label} should be less than 256 characters.`;
  return null;
};

// The defaults `ProviderAdd` starts from, carried over so a created provider
// looks the same whichever admin made it.
const EMPTY = {
  Id: 0,
  Name: '',
  Description: '',
  ContactName: '',
  PhoneNumber: '',
  Email: '',
  Type: 'Undefined',
  Category: '',
  CurrencyIsoCode: 'DKK',
  IsPublic: true
};

const ProviderInternal = ({ api, categories = [] }) => {
  const { providerId } = useParams();
  const navigate = useNavigate();

  const isNew = !providerId;

  const [provider, setProvider] = useState(isNew ? {} : null);
  const [form, setForm] = useState(isNew ? EMPTY : null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const toForm = data => ({
    Id: data.Id,
    Name: data.Name || '',
    Description: data.Description || '',
    ContactName: data.Contact?.Name || '',
    PhoneNumber: data.Contact?.PhoneNumber?.Number || '',
    Email: data.Contact?.EmailAddress?.Email || '',
    Type: data.Type || 'Undefined',
    Category: Array.isArray(data.Category) ? data.Category.join(', ') : (data.Category || ''),
    CurrencyIsoCode: data.CurrencyIsoCode || 'DKK',
    IsPublic: data.IsPublic !== false
  });

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const data = await api.providers.getProvider(providerId);
      if (!data || data.success === false || !data.Id) {
        setFailed(true);
        return;
      }
      setProvider(data);
      setForm(toForm(data));
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, providerId, isNew]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/providers">← All providers</BackToList>
        <div className="next-note next-note--error">
          Could not load this provider.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!provider || !form) return <div className="next-page next-muted">Loading…</div>;

  const errors = {
    Name: required(form.Name, 'Name'),
    Description: required(form.Description, 'Description'),
    Email: required(form.Email, 'Email'),
    ContactName: required(form.ContactName, 'Contact name'),
    PhoneNumber: required(form.PhoneNumber, 'Phone number'),
    Category: form.Category.trim() ? null : 'Category is required.'
  };
  const invalid = Object.values(errors).some(Boolean);

  const pristine = isNew ? EMPTY : toForm(provider);
  const dirty = Object.keys(form).some(key => form[key] !== pristine[key]);

  const set = (name, value) => setForm(previous => ({ ...previous, [name]: value }));

  const save = async event => {
    event.preventDefault();
    if (invalid || (!dirty && !isNew) || busy) return;

    setBusy(true);
    setNote(null);
    try {
      const payload = {
        ...form,
        // Both api functions join this; a string would throw.
        Category: form.Category
          .split(',')
          .map(value => value.trim())
          .filter(Boolean)
      };

      const updated = isNew
        ? await api.providers.saveNewProviderData(payload)
        : await api.providers.editProviderData(payload);

      if (isNew) {
        if (!updated || !updated.Id) {
          setNote({ tone: 'error', text: 'The provider was not created — the server answered without an id.' });
          return;
        }
        navigate(`/next/provider/${updated.Id}`);
        return;
      }

      // The endpoint answers the saved provider; falling back to a reload keeps
      // the page truthful if it ever answers something else.
      if (updated && updated.Id) {
        setProvider(updated);
        setForm(toForm(updated));
      } else {
        await load();
      }
      setNote({ tone: 'info', text: 'Provider saved.' });
    } catch (error) {
      setNote({
        tone: 'error',
        text: error?.response?.data?.ErrorMessage || error?.message
          || `Unable to ${isNew ? 'create' : 'save'} the provider.`
      });
    } finally {
      setBusy(false);
    }
  };

  const field = (name, label, { type = 'text', hint } = {}) => (
    <label className="next-filter" key={name}>
      <span className="next-filter__label">{label}</span>
      <input
        className="next-input"
        type={type}
        value={form[name]}
        onChange={event => set(name, event.target.value)}
      />
      {(errors[name] || hint) && (
        <span className={errors[name] ? 'next-form__error' : 'next-form__hint'}>
          {errors[name] || hint}
        </span>
      )}
    </label>
  );

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/providers">← All providers</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{isNew ? 'New provider' : (provider.Name || 'Provider')}</h1>
          <span className={`next-pill next-pill--${form.IsPublic ? 'ok' : 'neutral'}`}>
            {form.IsPublic ? 'Public' : 'Not public'}
          </span>
        </div>
        {!isNew && <span className="next-detail__id">{provider.Id}</span>}
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <form onSubmit={save}>
            <section className="next-card">
              <div className="next-card__head"><h2>Provider</h2></div>
              <div className="next-form__grid">
                {field('Name', 'Name')}
                {field('Description', 'Description')}

                <label className="next-filter">
                  <span className="next-filter__label">Type</span>
                  <select
                    className="next-input"
                    value={form.Type}
                    onChange={event => set('Type', event.target.value)}
                  >
                    {TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                  </select>
                </label>

                <label className="next-filter">
                  <span className="next-filter__label">Category</span>
                  <select
                    className="next-input"
                    value={form.Category}
                    onChange={event => set('Category', event.target.value)}
                  >
                    <option value="">—</option>
                    {/* What is stored today, when the picker cannot express it. */}
                    {form.Category && !categories.some(({ Name }) => Name === form.Category) && (
                      <option value={form.Category}>{form.Category} (current)</option>
                    )}
                    {categories.map(({ Name }) => <option key={Name} value={Name}>{Name}</option>)}
                  </select>
                  {errors.Category && <span className="next-form__error">{errors.Category}</span>}
                </label>

                {field('CurrencyIsoCode', 'Currency ISO code')}

                <label className="next-filter next-filter--check">
                  <input
                    type="checkbox"
                    checked={form.IsPublic}
                    onChange={event => set('IsPublic', event.target.checked)}
                  />
                  <span>Public</span>
                </label>
              </div>
            </section>

            <section className="next-card">
              <div className="next-card__head"><h2>Contact</h2></div>
              <div className="next-form__grid">
                {field('ContactName', 'Contact name')}
                {field('Email', 'Email', { type: 'email' })}
                {field('PhoneNumber', 'Phone number')}
              </div>

              <div className="next-form__actions">
                <button
                  type="submit"
                  className="next-btn next-btn--primary"
                  disabled={invalid || (!dirty && !isNew) || busy}
                  title={invalid ? 'Some fields need fixing first' : (!dirty && !isNew ? 'Nothing has changed' : undefined)}
                >
                  {busy ? 'Saving…' : (isNew ? 'Create provider' : 'Save changes')}
                </button>
                <button
                  type="button"
                  className="next-btn"
                  disabled={!dirty || busy}
                  onClick={() => setForm(isNew ? EMPTY : toForm(provider))}
                >
                  {isNew ? 'Clear' : 'Discard'}
                </button>
                {dirty && !isNew && <span className="next-muted">Unsaved changes</span>}
              </div>
            </section>

            {!isNew && (
            <section className="next-card">
              <div className="next-card__head"><h2>Read only</h2></div>
              <div className="next-facts">
                <div className="next-fact">
                  <span className="next-fact__label">Id</span>
                  <span className="next-fact__value"><Guid value={provider.Id} /></span>
                </div>
                <div className="next-fact">
                  <span className="next-fact__label">Created at (CET)</span>
                  <span className="next-fact__value">{fmtDate(provider.CreatedAtCet)}</span>
                </div>
              </div>
            </section>
            )}
          </form>
        </div>

        {!isNew && (
        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            {/* The old page's panel says "None" for both. Everything below is a
                search this provider's own id can already answer. */}
            <RelatedLinks
              items={[
                { label: 'Facilities', hint: 'run by this provider', to: `/next/facilities?providerId=${provider.Id}` },
                { label: 'Provider admins', hint: 'who can manage it', to: `/next/provider-admin?providerOrFacility=${encodeURIComponent(provider.Name || '')}` },
                { label: 'Participants', hint: 'at this provider’s venues', to: `/next/participants?providerId=${provider.Id}` },
                { label: 'Products', href: `/products?providerId=${provider.Id}` }
              ]}
            />
          </section>
        </aside>
        )}
      </div>
    </div>
  );
};

ProviderInternal.propTypes = {
  api: PropTypes.object.isRequired,
  categories: PropTypes.array
};

const Provider = withApi(
  connect(({ categories }) => ({ categories: categories || [] }), {})(ProviderInternal)
);

Provider.requiresLogin = true;

export default Provider;
